"use client"

import * as React from "react"
import { Plus } from "lucide-react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "../ui/select";
import { Button } from "./button";

interface ComboboxProps {
  options: { label: string; value: string; subtitle?: string }[];
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** CTA rendered under the empty state when nothing matches — e.g. "add a new option". */
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  /** Render the type-to-filter input. Hide for small static option sets (default true). */
  searchable?: boolean;
  className?: string;
  /** id for the trigger button, so an external <Label htmlFor> can target it. */
  id?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  emptyActionLabel,
  onEmptyAction,
  searchable = true,
  className,
  id,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  // Radix Select force-closes its content on window blur/resize. On mobile,
  // focusing the search input opens the virtual keyboard, which fires exactly
  // such a resize — the dropdown closed the instant the input was tapped,
  // before anything could be picked. Swallow close requests that land right
  // after a resize; genuine dismissals (tap outside, Escape, picking an item)
  // don't come with one, so they still close.
  const lastResizeAtRef = React.useRef(0);
  React.useEffect(() => {
    const markResize = () => { lastResizeAtRef.current = Date.now(); };
    window.addEventListener("resize", markResize);
    return () => window.removeEventListener("resize", markResize);
  }, []);

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    if (!nextOpen && Date.now() - lastResizeAtRef.current < 500) return;
    setOpen(nextOpen);
  }, []);

  const filteredOptions = React.useMemo(() => {
    if (!search.trim()) return options;
    const term = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(term) ||
        (o.subtitle && o.subtitle.toLowerCase().includes(term))
    );
  }, [options, search]);

  return (
    <Select
      value={value ? String(value) : undefined}
      open={open} onOpenChange={handleOpenChange}
      onValueChange={onChange}
    >
      <SelectTrigger id={id} className={className}>
        {value ? (
          <span className="truncate">
            {options.find(o => String(o.value) === String(value))?.label || placeholder || "Select job title"}
          </span>
        ) : (
          <SelectValue placeholder={placeholder || "Select job title"} />
        )}
      </SelectTrigger>
      {/* The popper viewport inherits the trigger's height (36px) from Radix's
          --radix-select-trigger-height var, which would clip the search box and list
          to a sliver. Neutralize it and let the CommandList scroll instead. */}
      <SelectContent
        className="[&_[data-radix-select-viewport]]:h-auto [&_[data-slot=command-list]]:max-h-[min(320px,var(--radix-select-content-available-height))]"
      >
        <SelectGroup className="hidden">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
        <Command shouldFilter={false}>
          {searchable && (
            <CommandInput
              placeholder={searchPlaceholder || "Search job title..."}
              className="border-none focus:ring-0 outline-none"
              value={search}
              onValueChange={setSearch}
            />
          )}
          <CommandList>
            {filteredOptions.length === 0 && (
              <CommandEmpty>
                {emptyActionLabel && onEmptyAction ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setOpen(false);
                      setSearch("");
                      onEmptyAction();
                    }}
                  >
                    <Plus size={14} className="mr-1.5" />{emptyActionLabel}
                  </Button>
                ) : (
                  <span>{emptyMessage || "No results found."}</span>
                )}
              </CommandEmpty>
            )}
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                  onSelect={() => {
                    onChange(option.value);
                    setSearch("");
                    setOpen(false);
                  }}
                >
                  <div className="flex flex-col">
                    <span>{option.label}</span>
                    {option.subtitle && (
                      <span className="text-[10px] text-gray-500">{option.subtitle}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </SelectContent>
    </Select>
  )
}
